import { useCallback, useEffect, useMemo, useReducer, useRef } from 'react'

import {
  applyAction,
  createRng,
  memoryEngine,
  REVEAL_MS,
  settleReveal,
  toMemoryView,
  type MemoryAction,
  type MemorySize,
  type MemoryState,
  type MemoryView,
} from '@hazel/shared'

import { api } from '../api/client.js'
import type { DealtRound } from './useRound.js'
import { useMoveLog } from './useMoveLog.js'
import { useMilestone } from './useMilestone.js'

const SOLO_PLAYER = 'solo'

const PLAYER = { id: SOLO_PLAYER, displayName: 'You', userId: null } as const

interface Game {
  readonly seed: string
  readonly state: MemoryState
  /** Only the flips the engine accepted, so a server replay follows the same path. */
  readonly actions: readonly MemoryAction[]
  readonly startedAt: number
}

type GameEvent =
  | { type: 'flip'; card: number; now: number }
  /** The pause after a miss has passed; put the cards back. */
  | { type: 'settle'; now: number }

function start(seed: string, size: MemorySize, now: number): Game {
  return {
    seed,
    state: memoryEngine.createInitialState({ size, startedAt: now }, [PLAYER], createRng(seed)),
    actions: [],
    startedAt: now,
  }
}

/** Pure, so React re-running it cannot record a game twice. */
function reduceGame(game: Game, event: GameEvent): Game {
  if (event.type === 'settle') {
    // Not an action, and deliberately not sent to the server: turning a missed
    // pair back over changes nothing a replay needs to know, and the engine
    // does it by itself on the next flip anyway.
    return { ...game, state: settleReveal(game.state, event.now) }
  }

  const action: MemoryAction = { type: 'flip', card: event.card }
  const outcome = applyAction(memoryEngine, game.state, action, {
    playerId: SOLO_PLAYER,
    now: event.now,
  })

  if (!outcome.ok) return game

  return { ...game, state: outcome.state, actions: [...game.actions, action] }
}

export interface Memory {
  readonly view: MemoryView
  readonly flip: (card: number) => void
  readonly again: () => void
  readonly finished: boolean
  /** The milestone this round crossed, if it crossed one. Null the rest of the time. */
  readonly milestone: number | null
  /** True while a missed pair is showing, so the board can stop taking taps. */
  readonly revealing: boolean
}

export function useMemory(round: DealtRound<{ size: MemorySize }>, onFinished: () => void): Memory {
  const [game, dispatch] = useReducer(reduceGame, round.seed, (seed) =>
    start(seed, round.config.size, Date.now()),
  )

  const moves = useMoveLog(round.roundId)

  const award = useMilestone()
  const recorded = useRef(false)
  const finished = game.state.status !== 'playing'
  const { revealUntil } = game.state

  /**
   * Puts a missed pair back once its moment has passed.
   *
   * The engine would do it on the next flip regardless, but nobody is going to
   * tap a board that has visibly frozen — without this the two cards sit there
   * until she happens to try again, which looks like the game has hung.
   */
  useEffect(() => {
    if (revealUntil === null) return

    const remaining = Math.max(0, revealUntil - Date.now())
    const timer = setTimeout(() => dispatch({ type: 'settle', now: Date.now() }), remaining)

    return () => clearTimeout(timer)
  }, [revealUntil])

  useEffect(() => {
    if (!finished || recorded.current) return
    recorded.current = true

    // Every flip reaches the server before it is asked to score them.
    void moves
      .flush()
      .then((told) => {
        // Nothing to score if the server never heard the whole round. The round
        // is not spent, so it can still be finished from another device — and
        // asking now would only have it refused.
        if (!told) return undefined

        return api('/api/plays', {
          method: 'POST',
          body: JSON.stringify({ roundId: round.roundId }),
        })
      })
      .then(award.notice)
      // A failure to record must not interrupt anything. She finished the game
      // either way, and a network blip is not her problem.
      .catch(() => undefined)
  }, [finished, round.roundId, moves, award.notice])

  return {
    view: useMemo(() => toMemoryView(game.state), [game.state]),
    flip: useCallback(
      (card: number) => {
        const now = Date.now()
        dispatch({ type: 'flip', card, now })
        // The engine may refuse it — a tap during the reveal, or on a card
        // already up. Recording it anyway is right: the server runs the same
        // rules and will refuse it in the same place, and leaving it out would
        // mean the server's account of the round is not the one she played.
        moves.record({ type: 'flip', card }, Math.max(0, now - game.startedAt))
      },
      [moves, game.startedAt],
    ),
    again: onFinished,
    finished,
    milestone: award.milestone,
    revealing: revealUntil !== null,
  }
}

export { REVEAL_MS }
